/**
 * Il Cockpit QA: il visualizzatore e' l'ancora, tutto il resto gli sta attorno.
 *
 * **Una gerarchia sola, e non e' una preferenza.** In alto una fascia di stato
 * compatta, al centro il fotogramma che prende tutto lo spazio che avanza, a
 * destra il pannello della decisione a larghezza fissa, in basso la coda di
 * orientamento. Il fotogramma e' l'unica cosa su cui si decide: ogni pixel che
 * gli si toglie e' una decisione presa con meno informazione. Per questo la tela
 * non scende mai sotto la sua misura e i due pannelli non crescono mai.
 *
 * **Il precedente era una composizione di passaggio** — tre colonne di uguale
 * dignita', contesto a sinistra, coda a sinistra, verdetto a destra — e si
 * vedeva: il fotogramma stava in mezzo a due elenchi, e la larghezza se la
 * dividevano in tre. Qui la coda scende sotto, il contesto sparisce come colonna
 * e riappare dentro la decisione, e cio' che resta a lato e' solo cio' che
 * accompagna un verdetto.
 *
 * **Il candidato si nomina per identita', non per posizione.** L'indice
 * nell'array non e' un indirizzo: la coda si rilegge a ogni ritorno della rete e
 * a ogni ripresa, e la terza riga di prima non e' il terzo candidato di dopo.
 * Tenere la posizione voleva dire tre cose sbagliate insieme — un collegamento
 * incollato che apriva un altro candidato, una rilettura che riportava
 * l'operatore su cio' che aveva appena deciso, e un annullamento che tornava
 * indietro di un passo invece che sul verdetto ritirato.
 *
 * **Questo modulo compone e coordina; non parla con la rete.** La sessione e i
 * lease sono di `useReviewSession` (GI-168), la consegna delle decisioni e'
 * della casella d'uscita (GI-172). Qui c'e' il collante: quale candidato si
 * guarda, quale modo della tastiera e' acceso, che cosa si dice quando qualcosa
 * non e' passato.
 *
 * **Il confine dei tasti e' strutturale, non un controllo.** Il gestore vive
 * sulla radice della tela: un tasto premuto altrove — barra dell'indirizzo,
 * intestazione della shell — non arriva. Dentro, i campi modificabili restano
 * campi di testo (`editable`), lo spazio e l'invio restano l'attivazione del
 * comando che ha il fuoco (`activating`), la ripetizione della tastiera non
 * diventa una raffica di verdetti (`held`), e le combinazioni appartengono al
 * navigatore tranne `Ctrl/Cmd+Z`, che il codice di disegno nomina.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'

import { ServiceError } from '../api'
import * as coverage from '../coverage'
import { levelOfRank, rankOf } from '../design/scales'
import { KeyHelp } from '../KeyHelp'
import { explainService } from '../i18n/explain'
import { useT } from '../i18n/LocaleProvider'
import type { Action, Mode } from '../keys'
import { actionFor, activating, commanding, editable, held, mutating } from '../keys'
import type { Intent, IntentState, Rejected } from '../outbox'
import { isDeclaration, outbox } from '../outbox'
import * as inspections from '../inspections'
import { indexPath, prewarm } from '../pyramid'
import * as reviews from '../reviews'
import { openSession, prelease, releaseLease } from '../reviewSessions'
import type {
  Box,
  Candidate,
  Frame,
  FrameFinding,
  FrameStrip as Strip,
  OpenedCandidate,
  Point,
  Queue,
  Reason,
} from '../types'
import type { Mark, Shape } from '../Viewer'
import { CandidateViewer } from './CandidateViewer'
import { CockpitHeader, Faults } from './CockpitHeader'
import type { Exited } from './DecisionPanel'
import { DecisionPanel } from './DecisionPanel'
import { FrameStrip } from './FrameStrip'
import { QueueStrip } from './QueueStrip'

/** Il fotogramma da cui si parte per una dichiarazione a mano: la pagina Foto
 *  lo apre per nome. */
export interface FrameRef {
  photoId: string
  filename: string | null
}
import { useReviewSession } from './useReviewSession'
import { useHeadSlot } from '../shells/headSlot'

export interface CockpitProps {
  inspectionId: string
  /** Chi lavora, per contare i primi verdetti (GI-281). Senza, la nota dei
   *  primi giorni non compare. */
  login?: string | null
  /** La sessione con cui il pool ha riservato questi candidati. Senza, il banco
   *  guarda il lavoro e non lo cambia. */
  reviewSessionId?: string | null
  /**
   * Il candidato che l'indirizzo nomina, per identita'.
   *
   * Ricaricare, incollare un collegamento, tornare indietro: tutti e tre devono
   * riaprire *quel* candidato. Se non c'e' piu' nel batch — lease scaduto,
   * ripreso da un collega — il banco ne scegle uno valido e lo dice
   * all'indirizzo.
   */
  candidateId?: string | null
  /** Dove il banco dice all'indirizzo su quale candidato si e' spostato. */
  onCandidate?: (detectionId: string | null) => void
  /** Il conflitto che l'indirizzo porta con se': si mostra sopra l'immagine,
   *  senza portarla via. */
  conflictId?: string | null
  /** L'uscita verso la ricerca libera. Assente quando il banco e' aperto fuori
   *  dall'instradamento: il comando resta visibile e spento, con la ragione.
   *  Porta il fotogramma che si sta guardando, se ce n'e' uno: la
   *  dichiarazione a mano riguarda quasi sempre cio' che si ha davanti. */
  onManualFinding?: (frame?: FrameRef) => void
  /** L'uscita verso la scheda dell'elemento del candidato corrente. Assente
   *  fuori dall'instradamento, come la ricerca libera. */
  onElement?: (candidate: Candidate) => void
  /** Dove il banco consegna la sessione appena aperta sul posto, perche'
   *  l'indirizzo la porti come se si fosse entrati dal pool (GI-246). */
  onSession?: (reviewSessionId: string) => void
  /** L'uscita verso gli altri fotogrammi dell'elemento del candidato (GI-248). */
  onElementFrames?: (candidate: Candidate) => void
}

/* Quanti secondi resta visibile la finestra d'annullamento. Cinque: il tempo di
 * accorgersi di aver premuto il tasto accanto, non quello di ripensarci. */
const UNDO_WINDOW_S = 5

/** Sotto quanti verdetti si e' ancora ai primi giorni. */
const NOVICE_VERDICTS = 20
const GIVEN = 'gridfly.verdicts.given'

function verdictsGiven(login: string): number {
  try {
    return Number(window.localStorage.getItem(`${GIVEN}.${login}`) ?? '0') || 0
  } catch {
    return NOVICE_VERDICTS
  }
}

function countVerdict(login: string): number {
  const next = verdictsGiven(login) + 1
  try {
    window.localStorage.setItem(`${GIVEN}.${login}`, String(next))
  } catch {
    // Senza memoria la nota resta: non e' un guasto.
  }
  return next
}

/* Il motivo del falso positivo, cioe' la voce dell'elenco che `D` sceglie da
 * sola. Il codice sta nel dizionario del servizio: qui c'e' il riferimento. */
const FALSE_POSITIVE = 'non_anomalia'

/* A quanti fotogrammi dal bordo della finestra il nastro si rilegge attorno al
 * corrente: prima che la mano arrivi al bordo, non quando ci sbatte. */
const STRIP_EDGE = 4

export function Cockpit({
  inspectionId,
  login = null,
  reviewSessionId = null,
  candidateId = null,
  onCandidate,
  conflictId = null,
  onManualFinding,
  onElement,
  onSession,
  onElementFrames,
}: CockpitProps): JSX.Element {
  const t = useT()
  const [state, setState] = useState<Queue | null>(null)
  const [reasons, setReasons] = useState<Reason[]>([])
  // Chi si guarda, per identita'. `null` con una coda piena significa una cosa
  // sola: si e' passato l'ultimo candidato del batch.
  const [chosen, setChosen] = useState<string | null>(candidateId)
  const [ended, setEnded] = useState(false)
  const [queueFailure, setQueueFailure] = useState<ServiceError | null>(null)
  const [reload, setReload] = useState(0)
  const [unsent, setUnsent] = useState(0)
  const [rejected, setRejected] = useState<Rejected[]>([])
  const [hushed, setHushed] = useState(0)
  const [hushConflict, setHushConflict] = useState<string | null>(null)
  const [states, setStates] = useState<Map<string, IntentState>>(new Map())
  // Vero quando il deposito ha risposto almeno una volta. La scelta di ripiego
  // aspetta questo: senza, sceglierebbe con un elenco di decisioni ancora vuoto
  // e il candidato aperto dipenderebbe da chi arriva primo — la coda o il
  // deposito. Un ripiego che cambia da un caricamento all'altro non e' un
  // ripiego deterministico.
  const [known, setKnown] = useState(false)
  // Il candidato che l'indirizzo nomina e la coda non porta: un punto gia'
  // deciso aperto dalla mappa, o uno in mano a un collega. Si prende per
  // identita' e si mostra lo stesso, col verdetto che ha. `missing` dice che
  // il servizio non lo conosce: solo allora il banco ripiega sul primo in coda.
  const [pinned, setPinned] = useState<OpenedCandidate | null>(null)
  const [missing, setMissing] = useState<string | null>(null)
  const [deferred, setDeferred] = useState<Set<string>>(new Set())
  const [last, setLast] = useState<string | null>(null)
  const [drawing, setDrawing] = useState<Shape | null>(null)
  const [drawn, setDrawn] = useState<Box | null>(null)
  // Il contorno a vertici (GI-228). Uno solo fra riquadro e contorno parte con
  // la conferma: chi disegna l'uno butta l'altro, e il servizio ricava dal
  // contorno il riquadro che lo contiene.
  const [polygon, setPolygon] = useState<Point[] | null>(null)
  const [mode, setMode] = useState<Mode>('normale')
  // Da che cosa si e' appena usciti. Spegnere un modo lasciava la regione viva
  // vuota, e un silenzio non e' un annuncio: chi ascolta restava a credere che
  // la cifra successiva dichiarasse ancora un rango.
  const [exited, setExited] = useState<Exited | null>(null)
  const [refusal, setRefusal] = useState<string | null>(null)
  const [window_, setWindow] = useState(0)
  // I verdetti dati da questa utenza, su questa macchina: sotto la soglia il
  // pannello dice a parole che un verdetto si annulla (GI-281). Il tasto c'e'
  // da sempre; la nota dice il patto — cinque secondi, poi le Anomalie.
  const [given, setGiven] = useState(() => (login === null ? NOVICE_VERDICTS : verdictsGiven(login)))
  const [queueClosed, setQueueClosed] = useState(false)
  const headSlot = useHeadSlot()
  const [helping, setHelping] = useState(false)
  const [hidden, setHidden] = useState(false)
  const [view, setView] = useState<{
    how: 'uno-a-uno' | 'inquadra' | 'trovata'
    nonce: number
  } | null>(null)
  // Vuoto finche' non si sa il nome: l'UUID nell'intestazione, anche per un
  // istante, e' l'identificatore interno che questa pagina esiste per non
  // mostrare.
  const [jobLabel, setJobLabel] = useState<string | undefined>(undefined)
  // Il nastro dei fotogrammi attorno a quello che si guarda (GI-265), e il
  // fotogramma guardato *al posto* del candidato: quello accanto nel volo,
  // senza un candidato in coda. Finche' si guarda altrove non si decide.
  const [strip, setStrip] = useState<Strip | null>(null)
  const [stripReading, setStripReading] = useState(false)
  const [look, setLook] = useState<Frame | null>(null)
  const [lookFindings, setLookFindings] = useState<FrameFinding[]>([])

  const canvas = useRef<HTMLDivElement>(null)
  // Anche in un riferimento e non solo nello stato: «R» e la cifra possono
  // arrivare nello stesso fotogramma — una mano veloce li batte in pochi
  // millesimi — e il gestore leggerebbe ancora il valore di prima, scambiando
  // il motivo per un rango. E' un verdetto diverso da quello inteso.
  const modeNow = useRef<Mode>('normale')
  // Un annullamento vale una volta sola finche' non arriva una nuova decisione:
  // senza questo fermo un Ctrl+Z ripetuto prende anche il verdetto prima.
  const undoArmed = useRef(true)
  const sessionNow = useRef<{ sessionId: string; reviewEpoch: number } | null>(null)
  const queueNow = useRef<Queue | null>(null)
  const stripNow = useRef<Strip | null>(null)
  stripNow.current = strip
  // A quale rilettura della coda il nastro e' allineato: una rilettura nuova
  // rilegge anche il nastro, dentro o fuori dalla finestra.
  const stripReload = useRef(-1)
  // In un riferimento perche' l'instradamento passa una funzione nuova a ogni
  // ridisegno: fra le dipendenze riscriverebbe l'indirizzo in tondo.
  const tellRoute = useRef(onCandidate)
  tellRoute.current = onCandidate
  /* Cio' che e' acceso adesso, in un riferimento perche' il ripristino lo legge
   * senza averlo fra le dipendenze: un effetto che dipendesse dal modo si
   * rifarebbe a ogni modo acceso, e annuncerebbe un'uscita a ogni entrata. */
  const armedNow = useRef<Exited | null>(null)
  armedNow.current =
    mode !== 'normale'
      ? mode
      : drawing !== null || drawn !== null || polygon !== null
        ? 'disegno'
        : null

  function enter(next: Mode): void {
    modeNow.current = next
    setMode(next)
    // Entrando in un modo l'uscita da quello di prima non ha piu' niente da
    // dire, e due voci sulla stessa cosa sono chiacchiera.
    if (next !== 'normale') setExited(null)
  }

  /** Porta lo sguardo su un candidato. Un posto solo perche' tastiera, comandi
   *  visibili, nastro e ritorni dopo un rifiuto devono spostarsi allo stesso
   *  modo: due strade divergono, e la differenza si scopre dal giornale. */
  const choose = useCallback((detectionId: string) => {
    setChosen(detectionId)
    setEnded(false)
    // Tornare su un candidato chiude lo sguardo su un altro fotogramma: si
    // decide su cio' che si vede.
    setLook(null)
  }, [])

  /**
   * Rimette l'operatore sul candidato che deve ancora decidere.
   *
   * Serve quando una decisione non e' stata presa in carico: la schermata era
   * gia' avanzata sulla promessa che lo fosse, e lasciarla avanti vorrebbe dire
   * che il candidato passa senza verdetto mentre l'operatore crede di averlo
   * dato. Si torna per identita': dopo un giro di J/K un passo indietro
   * riporterebbe su un candidato che non e' quello.
   */
  const back = useCallback(
    (detectionId: string | null) => {
      if (detectionId === null) return
      const there = queueNow.current?.candidates.some(
        (one) => one.detection_id === detectionId,
      )
      if (there !== true) return
      choose(detectionId)
    },
    [choose],
  )

  // Una sola casella d'uscita per tutto il turno: crearne una a ogni ridisegno
  // perderebbe i verdetti ancora da consegnare.
  const posta = useMemo(
    () =>
      outbox({
        onChange: setUnsent,
        onRejected: (one) => {
          // Persistente, non un avviso che sparisce da solo: e' l'unica traccia
          // che quel verdetto non e' passato.
          setRejected((were) => [...were, one])
          // Il nastro diceva «in mano a te» di un candidato il cui lease era
          // scaduto: dopo un rifiuto la coda si rilegge, e il nastro segue il
          // lease vero e non il momento in cui era stato preso.
          setReload((was) => was + 1)
          // Una dichiarazione di copertura non ha un candidato su cui tornare:
          // il banco non la mostra nemmeno, e cercarne uno riporterebbe
          // l'operatore su una riga che non c'entra.
          if (one.keep && !isDeclaration(one.intent)) back(one.intent.detectionId)
        },
        session: () => sessionNow.current,
      }),
    [back],
  )

  const reconcile = useCallback(
    (sessionId: string) => posta.reconcile(sessionId),
    [posta],
  )

  const session = useReviewSession({ inspectionId, reviewSessionId, reconcile })
  const sessionId = session.session?.review_session_id ?? null

  useEffect(() => {
    sessionNow.current = sessionId
      ? { sessionId, reviewEpoch: session.session?.review_epoch ?? 0 }
      : null
  }, [sessionId, session.session])

  useEffect(() => {
    let cancelled = false
    setJobLabel(undefined)
    inspections
      .commesse()
      .then((rows) => {
        if (cancelled) return
        const found = rows.find((one) => one.inspection_id === inspectionId)
        if (found) setJobLabel(String(found.external_inspection_id))
      })
      .catch(() => {
        // Resta l'identificatore interno: la fascia non deve sparire per un
        // elenco illeggibile.
      })
    return () => {
      cancelled = true
    }
  }, [inspectionId])

  useEffect(() => {
    queueNow.current = state
  }, [state])

  /* **Un rifiuto vecchio non e' l'ultima parola su un candidato che si ha in
   * mano, ne' su uno che non c'e' piu'.** Il rifiuto — «tornato in coda, va
   * ripreso» — parlava di un tentativo; quando la coda dice che il candidato
   * e' di nuovo in mano a questa sessione, o non lo porta piu' perche' e'
   * stato deciso, quel tentativo e' storia. Si legge dalla coda e non dal
   * batch dei lease: una sessione ripresa tiene i suoi candidati senza che
   * arrivi nessun lease nuovo, ed e' proprio il caso visto dal vivo il
   * 2.09.2026 — avviso di ore prima in cima, candidato in mano dato per «da
   * riprendere». */
  useEffect(() => {
    if (state === null) return
    const mine = state.candidates
      .filter((one) => one.lease_token != null)
      .map((one) => one.detection_id)
    const inQueue = new Set(state.candidates.map((one) => one.detection_id))
    const gone = [...states]
      .filter(([id, said]) => (said === 'rejected' || said === 'stale') && !inQueue.has(id))
      .map(([id]) => id)
    if (mine.length === 0 && gone.length === 0) return
    let alive = true
    // Sui candidati in mano si toglie solo il rifiuto di una sessione passata:
    // quello di questa sessione e' l'avviso vivo su cio' che si sta facendo, e
    // il candidato che la coda dice ancora «mio» puo' essere una lettura
    // vecchia di un istante. Su chi non c'e' piu' non ci sono riguardi.
    void Promise.all([posta.release(gone), posta.release(mine, sessionId ?? undefined)]).then((parts) => {
      const released = parts.flat()
      if (!alive || released.length === 0) return
      const set = new Set(released)
      setRejected((were) =>
        were.filter((one) => !('detectionId' in one.intent && set.has(one.intent.detectionId ?? ''))),
      )
      setHushed(0)
    })
    return () => {
      alive = false
    }
  }, [posta, state, states, sessionId])

  /* **Un verdetto partito cambia i conti.** «In attesa» e «decisi» vengono
   * dalla coda, e la coda si rileggeva solo alla ripresa: un verdetto
   * applicato lasciava i numeri di prima finche' non si tornava dal pool. Quando
   * la casella d'uscita si svuota di qualcosa, la coda si rilegge. */
  const unsentBefore = useRef(unsent)
  useEffect(() => {
    if (unsent < unsentBefore.current) setReload((was) => was + 1)
    unsentBefore.current = unsent
  }, [unsent])

  // Lo stato delle decisioni si rilegge quando il deposito cambia: tenerne una
  // copia qui vorrebbe dire un secondo elenco da mantenere allineato.
  useEffect(() => {
    let alive = true
    void posta.states().then((found) => {
      if (!alive) return
      setStates(found)
      setKnown(true)
    })
    return () => {
      alive = false
    }
  }, [posta, unsent, rejected])

  // Cio' che era rimasto da consegnare riparte all'apertura e al ritorno della
  // rete: e' il momento in cui il tentativo ha senso.
  useEffect(() => {
    void posta.flush()
    const again = (): void => void posta.flush()
    window.addEventListener('online', again)
    return () => window.removeEventListener('online', again)
  }, [posta])

  /* La coda si legge quando ripresa e acquisizione sono concluse: leggerla
   * prima mostrerebbe una coda senza i candidati appena riservati, cioe' vuota
   * su lavoro che si ha in mano.
   *
   * Non si azzera la posizione: la lettura si rifa' a ogni ritorno della rete e
   * a ogni ripresa, e azzerare rimetterebbe l'operatore sul candidato che ha
   * appena deciso. Cio' che si guarda lo dice l'identita', e questa lettura non
   * la tocca. */
  useEffect(() => {
    if (session.settled === 0) return
    let alive = true
    reviews
      .queue(inspectionId, sessionId)
      .then((found) => {
        if (!alive) return
        setState(found)
        // Il guasto di prima non resta appeso su una lettura riuscita.
        setQueueFailure(null)
      })
      .catch((error: unknown) => {
        if (alive && error instanceof ServiceError) setQueueFailure(error)
      })
    return () => {
      alive = false
    }
  }, [inspectionId, sessionId, session.settled, reload])

  useEffect(() => {
    reviews.reasons().then(setReasons).catch(() => setReasons([]))
  }, [])

  const candidates = state?.candidates ?? []
  const at = chosen === null ? -1 : candidates.findIndex((one) => one.detection_id === chosen)
  const opened: OpenedCandidate | null =
    at < 0 && pinned !== null && pinned.candidate.detection_id === chosen ? pinned : null
  const current: Candidate | undefined = at >= 0 ? candidates[at] : opened?.candidate

  /* Il candidato dell'indirizzo che la coda non porta si chiede per identita'.
   * Si aspetta la coda: prima, ogni candidato sembrerebbe assente. */
  useEffect(() => {
    if (candidateId === null || state === null) return
    if (state.candidates.some((one) => one.detection_id === candidateId)) return
    let alive = true
    reviews
      .candidate(candidateId)
      .then((opened) => {
        if (!alive) return
        setPinned(opened)
      })
      .catch(() => {
        if (!alive) return
        setMissing(candidateId)
      })
    return () => {
      alive = false
    }
  }, [candidateId, state])

  /* Il candidato dell'indirizzo vince, quando cambia: e' il tasto «indietro»,
   * un collegamento incollato, una scheda riaperta. */
  useEffect(() => {
    if (candidateId === null) return
    setChosen((was) => (was === candidateId ? was : candidateId))
    setEnded(false)
  }, [candidateId])

  /* E il banco dice all'indirizzo dove si e' spostato, perche' lo stato che si
   * puo' ricostruire da un collegamento e' quello che si e' scritto. */
  useEffect(() => {
    if (chosen === candidateId) return
    tellRoute.current?.(chosen)
  }, [chosen, candidateId])

  /* Se cio' che si guarda non c'e' (mai scelto, oppure scomparso dal batch) si
   * scegle il primo che aspetta ancora un verdetto: deterministico, e non un
   * candidato a caso ne' un banco vuoto su un batch pieno. Arrivare in fondo di
   * proposito e' un'altra cosa, e li' nessuna scelta e' quella giusta. */
  useEffect(() => {
    if (state === null || !known) return
    if (at >= 0) return
    if (chosen === null && ended) return
    // L'indirizzo nomina un candidato fuori coda: si aspetta la sua scheda, e
    // si ripiega solo quando il servizio dice che non esiste.
    if (chosen !== null && chosen === candidateId && missing !== candidateId) return
    const next = pending(state.candidates, states, deferred) ?? state.candidates[0]
    if (next) choose(next.detection_id)
  }, [state, known, at, chosen, ended, states, deferred, choose, candidateId, missing])

  /** Apre una sessione da qui: la stessa apertura del pool, con gli stessi
   *  lease. Un rifiuto — commessa congelata, servizio irraggiungibile — si
   *  dice nel pannello, dove si e' premuto. */
  const opening = useRef(false)
  const startHere = useCallback(async () => {
    if (opening.current || onSession === undefined) return
    opening.current = true
    setRefusal(null)
    try {
      const opened = await openSession(inspectionId)
      await prelease(opened.review_session_id, opened.max_active_leases)
      onSession(opened.review_session_id)
    } catch (error) {
      setRefusal(
        error instanceof ServiceError
          ? explainService(t, error.code, error.message)
          : t('common.unreachable'),
      )
    } finally {
      opening.current = false
    }
  }, [inspectionId, onSession, t])

  // Il fotogramma dopo si scalda mentre si guarda questo: i due livelli piu'
  // radi dei prossimi due candidati, e la loro miniatura. Chi passa con «J»
  // trova il fotogramma intero, non mezzo (GI-257).
  useEffect(() => {
    const here = queueNow.current?.candidates ?? []
    const where = current === undefined ? -1 : here.findIndex((one) => one.detection_id === current.detection_id)
    for (const next of here.slice(where + 1, where + 3)) {
      if (next.tiles_url) void prewarm(next.tiles_url, next.photo_id ? indexPath(next.photo_id) : null)
      if (next.thumbnail_url) new Image().src = next.thumbnail_url
    }
  }, [current, state])

  /* Il fotogramma che la tela mostra adesso: quello guardato, altrimenti quello
   * del candidato. E' l'identita' su cui si legge il nastro. */
  const shownId = look?.photo_id ?? current?.photo_id ?? null

  /* Il nastro si rilegge quando il fotogramma mostrato esce dalla finestra o le
   * si avvicina, e a ogni rilettura della coda (un verdetto cambia i conti
   * sulle miniature). Dentro la finestra si scorre senza chiedere niente: e' il
   * caso di ogni `L` e ogni `H`, e un viaggio a ogni passo farebbe aspettare
   * proprio il gesto che deve essere immediato. */
  useEffect(() => {
    if (shownId === null) {
      setStrip(null)
      return
    }
    const now = stripNow.current
    const where = now === null ? -1 : now.frames.findIndex((one) => one.photo_id === shownId)
    if (now !== null && where >= 0) {
      const nearStart = where < STRIP_EDGE && now.offset > 0
      const nearEnd =
        where > now.frames.length - 1 - STRIP_EDGE && now.offset + now.frames.length < now.total
      if (!nearStart && !nearEnd && reload === stripReload.current) return
    }
    stripReload.current = reload
    let alive = true
    setStripReading(true)
    coverage
      .frameStrip(inspectionId, shownId)
      .then((found) => {
        if (!alive) return
        setStrip(found)
        setStripReading(false)
      })
      .catch(() => {
        // Senza nastro il banco resta un banco: si decide sul candidato, e i
        // tasti dei fotogrammi dicono che il nastro non c'e'.
        if (alive) setStripReading(false)
      })
    return () => {
      alive = false
    }
  }, [inspectionId, shownId, reload])

  /* Le anomalie gia' registrate sul fotogramma guardato: si disegnano sopra,
   * perche' «questo fotogramma ha 2 anomalie» e' un numero, e per sapere dove
   * stanno non si deve aprire il banco due volte. */
  useEffect(() => {
    if (look === null) {
      setLookFindings([])
      return
    }
    let alive = true
    coverage
      .frameFindings(inspectionId, look.photo_id)
      .then((found) => alive && setLookFindings(found.findings))
      .catch(() => alive && setLookFindings([]))
    return () => {
      alive = false
    }
  }, [inspectionId, look, reload])

  // I due fotogrammi accanto nel volo si scaldano mentre si guarda questo, per
  // `H` e `L`: un passo che aspetta la piramide non e' un passo.
  useEffect(() => {
    if (strip === null || shownId === null) return
    const where = strip.frames.findIndex((one) => one.photo_id === shownId)
    for (const near of [strip.frames[where - 1], strip.frames[where + 1]]) {
      if (near?.tiles_url) void prewarm(near.tiles_url, indexPath(near.photo_id))
    }
  }, [strip, shownId])

  // Per identita' stabile: un elenco nuovo a ogni ridisegno farebbe rifare il
  // visualizzatore con gli stessi segni.
  const lookMarks: Mark[] = useMemo(
    () =>
      lookFindings
        .filter((one): one is FrameFinding & { box: Box } => one.box !== null)
        .map((one) => ({
          box: one.box,
          label: `${one.code} · ${rankOf(one.final_level ?? one.level).name}`,
          tone: one.decision === 'rejected' ? 'ink-3' : rankOf(one.final_level ?? one.level).tone,
        })),
    [lookFindings],
  )

  /** Porta lo sguardo su un fotogramma del nastro.
   *
   * Se il fotogramma ha un candidato nella coda si apre *quel* candidato: e'
   * lavoro in mano, e lo si decide. Altrimenti si guarda il fotogramma, con le
   * anomalie gia' registrate sopra e i verdetti spenti: nessun gesto qui
   * acquisisce lavoro. */
  const goFrame = useCallback(
    (frame: Frame) => {
      const here = queueNow.current?.candidates ?? []
      const there = here.find((one) => one.photo_id === frame.photo_id)
      if (there) {
        choose(there.detection_id)
        return
      }
      if (current?.photo_id === frame.photo_id) {
        setLook(null)
        return
      }
      setLook(frame)
      setRefusal(null)
    },
    [choose, current],
  )

  /** Un passo lungo il volo, con `H` e `L` o coi due bottoni del nastro. */
  const stepFrame = useCallback(
    (step: -1 | 1) => {
      const now = stripNow.current
      const where = now === null || shownId === null ? -1 : now.frames.findIndex((one) => one.photo_id === shownId)
      if (now === null || where < 0) {
        setRefusal(t('cockpit.noStrip'))
        return
      }
      const absolute = now.offset + where + step
      if (absolute < 0) {
        setRefusal(t('cockpit.firstFrame'))
        return
      }
      if (absolute >= now.total) {
        setRefusal(t('cockpit.lastFrame'))
        return
      }
      const target = now.frames[where + step]
      // Al bordo della finestra mentre la rilettura e' in volo: si dice, e il
      // tasto dopo trova la finestra nuova.
      if (!target) {
        setRefusal(t('cockpit.stripReading'))
        return
      }
      goFrame(target)
    },
    [shownId, goFrame, t],
  )

  /** Perche' questo banco non puo' decidere, se non puo'.
   *
   * Un candidato preso per identita' non si decide da qui: se ha un verdetto,
   * il verdetto e' quello, e per cambiarlo si passa dalla coda; se aspetta
   * ancora, non e' in questo batch e il gettone non c'e'. Mentre si guarda un
   * altro fotogramma non si decide affatto: il verdetto andrebbe su un
   * candidato che non e' sullo schermo. */
  const blocked: string | null =
    look !== null
      ? t('cockpit.lookingElsewhere')
      : opened !== null
        ? opened.decided !== null
          ? t('cockpit.alreadyDecided')
          : t('cockpit.notInBatch')
        : session.phase === 'osservazione'
          ? t('cockpit.observe')
          : session.phase === 'chiusa'
            ? t('cockpit.turnOver')
            : null

  /** Avanza al candidato dopo, o dichiara finito il batch. */
  const advance = useCallback(() => {
    const here = queueNow.current?.candidates ?? []
    const where = here.findIndex((one) => one.detection_id === chosen)
    const next = where < 0 ? undefined : here[where + 1]
    if (next) {
      choose(next.detection_id)
      return
    }
    setChosen(null)
    setEnded(true)
  }, [chosen, choose])

  const decide = useCallback(
    (action: Action) => {
      if (!action) return
      // Un fermo solo per tastiera, comandi visibili e ritorni: erano due
      // giudizi separati, e divergevano — il bottone risultava spento e lo
      // stesso verdetto passava dalla tastiera fino alla casella d'uscita, per
      // farsi rifiutare la'. Qui si dice subito, non si avanza, e nessuna
      // decisione viene scritta per poi essere ritirata.
      if (mutating(action) && blocked !== null) {
        setRefusal(blocked)
        return
      }
      if (look !== null) {
        // Su un fotogramma guardato non c'e' un riquadro da correggere ne' un
        // rango o un motivo da scegliere: sono gesti sul candidato, e il
        // candidato non e' sullo schermo.
        if (
          action.kind === 'draw' ||
          action.kind === 'polygon' ||
          action.kind === 'ranking' ||
          action.kind === 'rejecting'
        ) {
          setRefusal(t('cockpit.lookingElsewhere'))
          return
        }
        // `J` e `K` tornano prima di tutto al candidato: e' da li' che si era
        // partiti, e un passo in piu' porterebbe su un candidato mai visto.
        if (action.kind === 'next' || action.kind === 'previous') {
          setLook(null)
          setRefusal(null)
          return
        }
      }
      if (action.kind === 'next' || action.kind === 'previous') {
        const step = action.kind === 'next' ? 1 : -1
        const near = at < 0 ? undefined : candidates[at + step]
        if (!near) {
          setRefusal(action.kind === 'next' ? t('cockpit.lastCandidate') : t('cockpit.firstCandidate'))
          return
        }
        choose(near.detection_id)
        return
      }
      if (action.kind === 'rejecting') {
        // Non si rifiuta al buio: si accende l'elenco dei motivi, e la cifra
        // successiva ne sceglie uno.
        enter('motivo')
        setRefusal(null)
        return
      }
      if (action.kind === 'ranking') {
        enter('rango')
        setRefusal(null)
        return
      }
      if (action.kind === 'zoom') {
        // Il colpo distingue due ordini uguali: premere due volte lo stesso
        // tasto deve rifare il movimento, non essere ignorato.
        setView((was) => ({ how: action.how, nonce: (was?.nonce ?? 0) + 1 }))
        return
      }
      if (action.kind === 'overlays') {
        setHidden(!action.visible)
        return
      }
      if (action.kind === 'help') {
        setHelping((was) => !was)
        return
      }
      if (action.kind === 'panel') {
        setQueueClosed((was) => !was)
        return
      }
      if (action.kind === 'nextFrame' || action.kind === 'previousFrame') {
        // Un passo lungo il volo, della stessa camera (ADR-006): lo scatto
        // prima e quello dopo sono il contesto in cui un'anomalia si giudica.
        stepFrame(action.kind === 'nextFrame' ? 1 : -1)
        return
      }
      if (action.kind === 'draw') {
        setDrawing((was) => (was === 'box' ? null : 'box'))
        return
      }
      if (action.kind === 'polygon') {
        setDrawing((was) => (was === 'polygon' ? null : 'polygon'))
        return
      }
      if (action.kind === 'cancel') {
        enter('normale')
        setHelping(false)
        setRefusal(null)
        // E si dice da che cosa si e' usciti. Nulla di acceso, nessun annuncio:
        // chi lavora batte `Esc` per sicurezza prima di guardare, e sentirsi
        // dire ogni volta di essere uscito da qualcosa insegna a non ascoltare
        // piu' la regione viva.
        setExited(armedNow.current)
        // Si esce dal disegno e si butta il riquadro appena tracciato: e' il
        // gesto di chi si e' accorto di aver sbagliato, e costa un tasto.
        setDrawing(null)
        setDrawn(null)
        setPolygon(null)
        return
      }
      if (action.kind === 'manual') {
        if (!onManualFinding) {
          setRefusal(t('cockpit.noManualExit'))
          return
        }
        // Con il fotogramma che si ha davanti: la dichiarazione a mano riguarda
        // quasi sempre cio' che si sta guardando, e la pagina Foto lo apre.
        const from: FrameRef | undefined =
          look !== null
            ? { photoId: look.photo_id, filename: look.filename }
            : current?.photo_id
              ? { photoId: current.photo_id, filename: current.photo_filename }
              : undefined
        onManualFinding(from)
        return
      }
      if (action.kind === 'element') {
        if (!onElement) {
          setRefusal(t('cockpit.noElementExit'))
          return
        }
        // Un candidato senza elemento non ha una scheda da aprire: lo si dice,
        // invece di aprire una pagina che direbbe «sconosciuto».
        if (current === undefined || current.element_id === null) {
          setRefusal(t('cockpit.noElement'))
          return
        }
        onElement(current)
        return
      }
      if (action.kind === 'undo') {
        if (!undoArmed.current) return
        undoArmed.current = false
        // Anche senza sapere su che cosa si era deciso: la pagina ricaricata ha
        // perso la memoria dell'ultimo candidato, non i cinque secondi per
        // rimediare. Il deposito sa quale verdetto ritirare.
        posta.add({ kind: 'undo', detectionId: last })
        setWindow(0)
        if (!last) return
        setLast(null)
        // Sul candidato annullato, non un passo indietro: dopo un giro di J/K la
        // posizione di prima e' quella di un altro candidato, e si rifarebbe il
        // verdetto sbagliato lasciando il proprio senza.
        back(last)
        return
      }
      if (!current) return

      if (action.kind === 'defer') {
        const token = current.lease_token
        if (!token) {
          setRefusal(t('cockpit.nothingToDefer'))
          return
        }
        if (!session.online) {
          setRefusal(t('cockpit.offlineDefer'))
          return
        }
        setDeferred((were) => new Set(were).add(current.detection_id))
        releaseLease(token).catch(() => {
          setRefusal(t('cockpit.deferFailed'))
        })
        setRefusal(null)
        advance()
        return
      }

      if (action.kind === 'confirm' && action.rank !== null && levelOfRank(action.rank) === null) {
        // Prima si confermava al livello *proposto dal modello*: l'operatore
        // dichiarava S1 e nel giornale finiva il livello del modello. Un
        // verdetto diverso da quello inteso, e nessuno se ne accorgeva.
        setRefusal(t('cockpit.s1Blocked'))
        return
      }

      const intent = intentFor(action, current, reasons, drawn, polygon)
      if (!intent) return

      posta.add(intent)
      undoArmed.current = true
      setLast(current.detection_id)
      setRefusal(null)
      // La finestra d'annullamento parte adesso: cinque secondi in cui l'errore
      // costa un tasto invece di una ricerca nel giornale.
      setWindow(UNDO_WINDOW_S)
      if (login !== null) setGiven(countVerdict(login))
      // Si avanza subito, senza aspettare la consegna: un verdetto e' una
      // decisione gia' presa, e farla attendere la rete mette la latenza dentro
      // il tempo di lavoro. Il modo, il riquadro e la ragione dell'ultimo gesto
      // se ne vanno col candidato: ci pensa un posto solo, quello che guarda
      // l'identita' cambiare.
      advance()
    },
    [
      advance,
      at,
      blocked,
      candidates,
      choose,
      current,
      drawn,
      polygon,
      back,
      last,
      look,
      onManualFinding,
      onElement,
      posta,
      reasons,
      session.online,
      stepFrame,
      t,
    ],
  )

  /* Il modo armato, il riquadro tracciato e la ragione dell'ultimo gesto
   * appartengono al candidato su cui si e' battuto. Portarseli dietro fa
   * dichiarare un rango sul candidato dopo — che sta altrove, con un altro
   * codice — o leggere una ragione che riguarda un altro. Un posto solo per il
   * ripristino, perche' i modi da cui usciva erano cinque e ognuno se ne
   * ricordava a parte. */
  useEffect(() => {
    enter('normale')
    setRefusal(null)
    setDrawing(null)
    setDrawn(null)
    setPolygon(null)
    // Il candidato nuovo arriva senza modo acceso, e va detto: `setExited` qui
    // vale in entrambi i versi — annuncia l'uscita se qualcosa era acceso, e
    // ripulisce l'annuncio di prima se non lo era, cosi' nessuna voce resta
    // appesa da un candidato all'altro. Lo stesso quando cambia il fotogramma
    // guardato: un riquadro tracciato su uno scatto non vale su quello dopo.
    setExited(armedNow.current)
  }, [chosen, look?.photo_id])

  // Il contatore della finestra. Scende di un secondo per volta perche' e' cio'
  // che l'operatore legge.
  useEffect(() => {
    if (window_ <= 0) return
    const tick = setTimeout(() => setWindow((was) => was - 1), 1000)
    return () => clearTimeout(tick)
  }, [window_])

  /* Il fuoco va al lavoro quando il lavoro arriva, e solo se nessuno lo ha
   * chiesto altrove: la shell mette il fuoco sul titolo per annunciare la
   * route, e da li' i tasti del banco non arriverebbero da nessuna parte. Se
   * invece l'operatore ha gia' scelto un comando, il fuoco resta suo. */
  const grabbed = useRef(false)
  useEffect(() => {
    if (grabbed.current || !current) return
    const active = document.activeElement
    const idle = active === null || active === document.body || active.tagName === 'H1'
    if (!idle) return
    grabbed.current = true
    canvas.current?.focus()
  }, [current])

  /* Cio' che non e' passato, in un elenco solo e senza comandi dentro: e' la
   * regione viva che annuncia i guasti, e una regione viva che contiene bottoni
   * li rilegge tutti a ogni cambiamento. I comandi stanno nei veli. */
  const alerts = [
    ...(session.failure
      ? [
          {
            message: explainService(t, session.failure.code, session.failure.message),
            code: session.failure.code,
          },
        ]
      : []),
    ...(queueFailure
      ? [
          {
            message: t('cockpit.queueReadFailed', {
              message: explainService(t, queueFailure.code, queueFailure.message),
            }),
            code: queueFailure.code,
          },
        ]
      : []),
    ...rejected.map((one) => ({
      message: t('cockpit.verdictUnrecorded', {
        message: explainService(t, one.code, one.message),
      }),
      code: one.code,
    })),
  ]

  /* I veli: sopra l'immagine e non al posto suo, perche' spesso e' guardando
   * che l'operatore capisce se il guasto lo riguarda. Ognuno si chiude, e
   * quello che si puo' riprovare porta il suo comando. */
  const unhushed = rejected.slice(hushed)
  const spoiled = unhushed[unhushed.length - 1]
  const veils = [
    conflictId !== null && hushConflict !== conflictId ? (
      <div className="cockpit-velo" role="group" aria-label={t('cockpit.routeConflict')} key="conflitto">
        <p>
          {t('cockpit.routeConflictActive')} <code>{conflictId}</code>.
        </p>
        <a
          className="cockpit-azione"
          href={`/inspections/${inspectionId}/queue?conflict=${encodeURIComponent(conflictId)}`}
        >
          {t('cockpit.openConflict')}
        </a>
        <button
          type="button"
          className="cockpit-azione"
          onClick={() => setHushConflict(conflictId)}
        >
          {t('cockpit.hideNotice')}
        </button>
      </div>
    ) : null,
    queueFailure !== null ? (
      <div className="cockpit-velo" role="group" aria-label={t('cockpit.queueUnread')} key="coda">
        <p>
          {t('cockpit.queueUnreadBody', {
            detail: explainService(t, queueFailure.code, queueFailure.message),
          })}
          {/* Codice e richiesta per chi indaga, in tono tenue: la frase sopra
              e' per chi lavora. Stessa divisione degli altri veli. */}
          <span className="tenue cockpit-codice">
            {' '}
            {queueFailure.code}
            {queueFailure.requestId === null
              ? ''
              : ` · ${t('pool.requestId', { id: queueFailure.requestId })}`}
          </span>
        </p>
        <p className="tenue">{t('cockpit.queueUnreadHint')}</p>
        <button
          type="button"
          className="cockpit-azione"
          onClick={() => {
            setQueueFailure(null)
            setReload((was) => was + 1)
          }}
        >
          {t('cockpit.rereadQueue')}
        </button>
        <button type="button" className="cockpit-azione" onClick={() => setQueueFailure(null)}>
          {t('cockpit.hideNotice')}
        </button>
      </div>
    ) : null,
    spoiled ? (
      <div className="cockpit-velo" role="group" aria-label={t('cockpit.verdictUnrecordedTitle')} key="verdetto">
        <p>
          {t('cockpit.verdictUnrecorded', {
            message: explainService(t, spoiled.code, spoiled.message),
          })}
          {/* Il codice per chi indaga, la frase per chi lavora: due voci,
              non una frase con un codice appeso. */}
          {spoiled.code !== null && <span className="tenue cockpit-codice"> {spoiled.code}</span>}
        </p>
        <p className="tenue">
          {spoiled.keep ? t('cockpit.candidateBack') : t('cockpit.retryFromStrip')}
        </p>
        <button
          type="button"
          className="cockpit-azione"
          onClick={() => setHushed(rejected.length)}
        >
          {t('cockpit.hideNotice')}
        </button>
      </div>
    ) : null,
  ].filter((one) => one !== null)

  // La riga di testa del banco: la fascia di stato e, accanto, il nastro dei
  // lease come chip. Un frammento solo, perche' va in un posto solo — il nodo
  // che la shell offre nella sua intestazione, o la cima della griglia.
  const head = (
    <>
      <CockpitHeader
        jobLabel={jobLabel}
        expiresAt={session.session?.expires_at ?? null}
        waiting={state?.waiting ?? 0}
        decided={state?.decided ?? 0}
        unsent={unsent}
        phase={session.phase}
        degraded={session.degraded}
        online={session.online}
        reconciling={session.reconciling}
        resumed={session.resumed}
        contended={session.contended}
        exhausted={ended && candidates.length > 0}
        onClose={() => void session.close()}
        onHelp={() => setHelping(true)}
      />
      <QueueStrip
        // Il candidato preso per identita' sta sul nastro da solo: un nastro
        // che dice «nessun candidato in mano» sotto un candidato aperto e' una
        // contraddizione, non un'informazione.
        candidates={opened !== null ? [opened.candidate] : candidates}
        at={opened !== null ? 0 : at}
        states={states}
        deferred={deferred}
        mine={sessionId}
        onGo={(detectionId) => choose(detectionId)}
      />
    </>
  )

  return (
    <div
      className="cockpit"
      data-theme="cockpit"
      ref={canvas}
      // Ricevibile dal fuoco ma fuori dalla tabulazione: e' un contenitore, e
      // una tappa in piu' fra i comandi si paga a ogni candidato.
      tabIndex={-1}
      onKeyDown={(event) => {
        // La mappa dei tasti e' modale: sotto di lei il banco non sente niente,
        // altrimenti una «A» battuta mentre si legge la mappa confermerebbe un
        // candidato che nessuno sta guardando. `Esc` lo prende la mappa.
        if (helping) return
        // Dove si scrive, i tasti restano lettere.
        if (editable(event.target)) return
        // Sul comando che ha il fuoco, lo spazio e l'invio sono il clic: la
        // tela non li prende, altrimenti il bottone non si attiva e resta
        // premibile solo col puntatore.
        if (activating(event) && commanding(event.target)) return
        const action = actionFor(event, modeNow.current)
        if (!action) return
        event.preventDefault()
        // La ripetizione della tastiera non e' un gesto: un dito appoggiato su
        // «A» manderebbe una raffica di conferme lungo il batch, e
        // l'annullamento vale una volta sola. Passa solo cio' che si tiene
        // premuto di proposito.
        if (event.repeat && !held(action)) return
        decide(action)
      }}
      onKeyUp={(event) => {
        // Lo spazio si tiene premuto, quindi ha bisogno anche del rilascio:
        // senza, i disegni resterebbero via e l'operatore vedrebbe un
        // fotogramma nudo senza sapere perche'.
        if (event.key === ' ') setHidden(false)
      }}
    >
      {helping && <KeyHelp onClose={() => setHelping(false)} />}

      {/* La fascia di stato e il nastro dei lease stanno nella riga di testa
          della shell (GI-266): qui si scrivono attraverso il portale. Senza
          shell attorno — nelle prove, o montato da solo — stanno in cima alla
          griglia, nello stesso ordine di tabulazione. */}
      {headSlot === null ? <div className="cockpit-testa">{head}</div> : createPortal(head, headSlot)}

      <Faults alerts={alerts} />

      <CandidateViewer
        inspectionId={inspectionId}
        candidate={current ?? null}
        look={look}
        marks={lookMarks}
        onAction={decide}
        drawing={drawing}
        box={drawn ?? current?.box ?? null}
        onDrawn={(box) => {
          setDrawn(box)
          setPolygon(null)
        }}
        polygon={polygon}
        onDrawnPolygon={(points) => {
          setPolygon(points)
          setDrawn(null)
          // Chiudere il contorno e' un gesto esplicito: il modo si spegne da
          // solo, e il clic successivo torna a spostare l'immagine.
          setDrawing(null)
        }}
        mode={modeName(drawing, mode, t)}
        hidden={hidden}
        view={view}
        veil={veils.length === 0 ? null : <div className="cockpit-veli">{veils}</div>}
        empty={
          state === null ? (
            queueFailure === null ? (
              <p className="tenue">{t('cockpit.readingQueue')}</p>
            ) : (
              <p className="tenue">{t('cockpit.queueUnreadEmpty')}</p>
            )
          ) : session.contended ? (
            <p className="tenue">{t('cockpit.contendedEmpty')}</p>
          ) : missing !== null && missing === candidateId ? (
            <p className="tenue">{t('cockpit.candidateGone')}</p>
          ) : candidateId !== null && chosen === candidateId && pinned === null ? (
            <p className="tenue">{t('cockpit.readingCandidate')}</p>
          ) : state.candidates.length === 0 ? (
            <p className="tenue">
              {t('cockpit.noneWaiting')}{' '}
              <Link to={`/inspections/${inspectionId}`}>{t('cockpit.noneWaitingNext')}</Link>
            </p>
          ) : (
            <p className="tenue">{t('cockpit.batchDone')}</p>
          )
        }
      />

      <DecisionPanel
        candidate={current ?? null}
        reasons={reasons}
        mode={mode}
        exited={exited}
        intent={current ? (states.get(current.detection_id) ?? null) : null}
        refusal={refusal}
        blocked={blocked}
        decided={opened?.decided ?? null}
        mine={sessionId}
        undoSeconds={window_}
        novice={given < NOVICE_VERDICTS}
        drawing={drawing}
        redrawn={polygon !== null ? 'polygon' : drawn !== null ? 'box' : null}
        manual={onManualFinding !== undefined}
        element={onElement !== undefined && current !== undefined && current.element_id !== null}
        observing={session.phase === 'osservazione'}
        onSession={onSession === undefined ? undefined : () => void startHere()}
        onFrames={
          onElementFrames !== undefined && current !== undefined && current.element_id !== null
            ? () => onElementFrames(current)
            : undefined
        }
        onAction={decide}
      />

      <FrameStrip
        strip={strip}
        shownId={shownId}
        reading={stripReading}
        closed={queueClosed}
        onGo={goFrame}
        onStep={stepFrame}
      />
    </div>
  )
}

/** Il primo candidato che aspetta ancora un verdetto.
 *
 * E' la scelta di ripiego quando l'identita' dell'indirizzo non c'e' piu': non
 * il primo dell'elenco, che l'operatore ha probabilmente gia' deciso, e non uno
 * a caso. Un verdetto ritirato o respinto conta come «da decidere», perche' su
 * quel candidato manca ancora una decisione valida. */
function pending(
  candidates: Candidate[],
  states: Map<string, IntentState>,
  deferred: Set<string>,
): Candidate | undefined {
  return candidates.find((one) => {
    if (deferred.has(one.detection_id)) return false
    const said = states.get(one.detection_id)
    return said === undefined || said === 'withdrawn' || said === 'rejected' || said === 'stale'
  })
}

/** Il verdetto che corrisponde all'azione, se ne corrisponde uno. */
function intentFor(
  action: Action,
  candidate: Candidate,
  reasons: Reason[],
  drawn: Box | null,
  polygon: Point[] | null,
): Intent | null {
  // Il gettone e la versione viaggiano con ogni decisione presa dentro una
  // sessione: sono la prova di aver preso quel candidato e lo stato da cui la
  // decisione parte. Senza, il servizio non puo' distinguere un verdetto
  // legittimo da uno scritto sopra il lavoro di un collega.
  const reserved = {
    leaseToken: candidate.lease_token ?? null,
    candidateVersion: candidate.candidate_version ?? 0,
  }
  if (action?.kind === 'confirm') {
    // La cifra e' un rango, non un livello: il livello e' la grandezza che
    // conosce il committente, e la conversione sta in un posto solo.
    const chosen = action.rank === null ? null : levelOfRank(action.rank)
    return {
      kind: 'confirm',
      detectionId: candidate.detection_id,
      level: chosen ?? candidate.level ?? 2,
      // Il riquadro ridisegnato viaggia con la conferma: su un rifiuto non
      // vorrebbe dire niente, perche' l'anomalia non c'e'.
      box: drawn ?? undefined,
      polygon: polygon ?? undefined,
      ...reserved,
    }
  }
  if (action?.kind === 'falsePositive') {
    // Il falso positivo e' il rifiuto piu' frequente e ha un tasto suo. Il
    // motivo non si inventa: e' la voce dell'elenco del servizio che dice «non
    // e' un'anomalia».
    const reason = reasons.find((one) => one.code === FALSE_POSITIVE)
    return reason
      ? {
          kind: 'reject',
          detectionId: candidate.detection_id,
          reasonCode: reason.code,
          ...reserved,
        }
      : null
  }
  if (action?.kind !== 'reject') return null
  const reason = reasons.find((one) => one.shortcut === action.shortcut)
  // Una cifra senza motivo non fa nulla, e non e' un errore: l'elenco cambia
  // nel tempo, e un tasto che non c'e' piu' non deve fermare il lavoro.
  return reason
    ? { kind: 'reject', detectionId: candidate.detection_id, reasonCode: reason.code, ...reserved }
    : null
}

/** Il nome del modo in corso, o nulla se si e' nel modo base.
 *
 * Un modo che non si vede e' una sorgente di battute sbagliate: e' il motivo
 * per cui il codice di disegno lo vuole scritto sul fotogramma (§ 6.2). */
function modeName(
  drawing: Shape | null,
  mode: Mode,
  t: (
    key:
      | 'cockpit.modeDraw'
      | 'cockpit.modeDrawPolygon'
      | 'cockpit.modePickReason'
      | 'cockpit.modePickRank',
  ) => string,
): string | null {
  if (drawing === 'polygon') return t('cockpit.modeDrawPolygon')
  if (drawing === 'box') return t('cockpit.modeDraw')
  if (mode === 'motivo') return t('cockpit.modePickReason')
  if (mode === 'rango') return t('cockpit.modePickRank')
  return null
}
